import { Box, Center, Flex, IconButton } from '@chakra-ui/react';
import { IMAGE_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { areEqual } from 'react-window';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData } from 'widgets/ImageWidget/ImageWidget.helpers';

import { getInitialWidgetPosition, store } from '../../../../store';
import { selectInfographHeightPx, selectInfographWidthPx } from '../../../../store/infographSelector';
import { COLUMN_NUM, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../InfiniteGrid/InfiniteGrid.config';
import { DeleteUploadImageIcon } from './DeleteUploadImageIcon';
import { HandleClickDelete, HandleClickUserImage, UserImageCellProps, UserImageData } from './UploadMenu.helper';
import { useUploadMenu } from './UploadMenuContext';
import { UserImage } from './UserImage';

const DEFAULT_STYLE = {
  buttonHover: { opacity: 0.7 },
};

export const UserImageCell = memo(({ columnIndex, rowIndex, style, data }: UserImageCellProps<UserImageData>) => {
  const [isHoverOnDelete, setIsHoverOnDelete] = useState(false);
  const [imageBtnOnFocus, setImageBtnOnFocus] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);

  const cellIdx = rowIndex * COLUMN_NUM + columnIndex;
  const { uploadImages, onOpenDeleteAlertDialog, setDeleteMediaId, disableDragDrop } = data;
  const imageData = uploadImages![cellIdx];

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  const { addUploadImageWidget } = useUploadMenu();

  const createWidgetData = useCallback(() => {
    if (!imageRef.current) return;

    const infographWidthPx = selectInfographWidthPx(store.getState());
    const infographHeightPx = selectInfographHeightPx(store.getState());
    const infographSize = { width: infographWidthPx, height: infographHeightPx };
    const { top, left } = getInitialWidgetPosition();
    const { src, naturalWidth: width, naturalHeight: height } = imageRef.current;

    const widget = generateDefaultData(src, { width, height }, infographSize, top, left);
    return widget;
  }, []);

  const onClickUserImage: HandleClickUserImage = useCallback(() => {
    const widget = createWidgetData();
    if (!widget) return;

    addUploadImageWidget(widget);

    Mixpanel.track(IMAGE_CREATED.eventName, {
      from: IMAGE_CREATED.from.Upload,
    });
  }, [addUploadImageWidget, createWidgetData]);

  const onClickDelete: HandleClickDelete = () => {
    if (!!imageData.mediaId) {
      setDeleteMediaId(imageData.mediaId);
      onOpenDeleteAlertDialog();
    }
  };

  const onImageBtnFocus = () => setImageBtnOnFocus(true);
  // delay removing focus from image button for 100 ms so that focus can be moved to the delete button. Otherwise, the focus would be lost as when imageBtnOnFocus turns to false, delete button would be removed from DOM
  const onImageBtnBlur = () => setTimeout(() => setImageBtnOnFocus(false), 100);

  return (
    imageData && (
      <Flex data-group style={style}>
        <WidgetDragDropWrapper
          isDraggable={!disableDragDrop}
          widgetData={createWidgetData()}
          type={WidgetType.Image}
          dragImage={
            <Box h={style.height} w={style.width}>
              <Center>
                {/* ignore fallback for ghost preview images */}
                <UserImage idx={imageData.idx} url={imageData.url} imageRef={imageRef} ignoreFallback={true} />
              </Center>
            </Box>
          }
          dragImageOffset={{ x: THUMBNAIL_WIDTH / 2, y: THUMBNAIL_HEIGHT / 2 }}
          trackingEventData={{
            event_name: IMAGE_CREATED.eventName,
            properties: {
              from: IMAGE_CREATED.from.Upload,
            },
          }}
        >
          <IconButton
            aria-label={t('sideBar.widget_menu_upload.userImageLabel')}
            variant='icon-btn-side-menu'
            h={style.height}
            w={style.width}
            bgColor={isHoverOnDelete ? 'hover.gray' : 'none'}
            onClick={onClickUserImage}
            onFocus={onImageBtnFocus}
            onBlur={onImageBtnBlur}
            data-testid='insert-user-image'
            p='2'
          >
            <UserImage
              idx={imageData.idx}
              url={imageData.url}
              imageRef={imageRef}
              imageProps={{
                _groupHover: DEFAULT_STYLE.buttonHover,
                _groupFocusVisible: DEFAULT_STYLE.buttonHover,
                borderRadius: 'base',
              }}
            />
          </IconButton>
        </WidgetDragDropWrapper>
        <DeleteUploadImageIcon
          imageBtnOnFocus={imageBtnOnFocus}
          onClickDelete={onClickDelete}
          setHover={setIsHoverOnDelete}
        />
      </Flex>
    )
  );
}, areEqual);
UserImageCell.displayName = 'UserImageCell';
